import { useConfig } from '#~/config/hooks.js';
import { isElectronApp } from '#~/electron/utils.js';
import { cn } from '#~/utils/cn.js';
import { type ReactElement } from 'react';

// Declare the build date constant that will be injected at build time
declare const __BUILD_DATE__: string;
declare const __BUILD_BRANCH__: string;
declare const __BUILD_COMMIT__: string;

interface InfoCardProps {
  title: string;
  items: Array<{
    label: string;
    value: string | boolean | undefined;
  }>;
}

const InfoCard = ({ title, items }: InfoCardProps): ReactElement => {
  return (
    <div className='border-gray-200 rounded-lg border p-4'>
      <h2 className='font-semibold mb-4 text-lg'>{title}</h2>
      <div className='space-y-2'>
        {items.map((item, index) => (
          <div key={index} className='flex'>
            <span className='w-1/3 font-medium'>{item.label}:</span>
            <span
              className={cn(
                'flex-1',
                typeof item.value === 'boolean'
                  ? item.value
                    ? 'text-green-600'
                    : 'text-red-600'
                  : ''
              )}
            >
              {typeof item.value === 'boolean'
                ? item.value
                  ? 'Yes'
                  : 'No'
                : (item.value ?? 'N/A')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

interface BuildInfoProps {
  readonly konamiUnlocked?: boolean;
}

export const BuildInfo = ({ konamiUnlocked }: BuildInfoProps): ReactElement => {
  const config = useConfig();

  // Retrieve package.json version from the config
  const { phase, vendor, version } = config;
  const isElectron = isElectronApp();
  // Use the injected build date instead of generating it at runtime
  const buildDate = typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__ : 'Unknown';
  const buildBranch = typeof __BUILD_BRANCH__ !== 'undefined' ? __BUILD_BRANCH__ : 'Unknown';
  const buildCommit = typeof __BUILD_COMMIT__ !== 'undefined' ? __BUILD_COMMIT__ : 'Unknown';

  return (
    <div className='max-w-4xl mx-auto flex min-h-screen flex-col items-center justify-center p-8'>
      <h1 className='mb-6 text-center text-2xl font-bold'>Build Information</h1>

      {konamiUnlocked && (
        <span className='bg-amber-100 px-3 py-1 text-amber-800 mb-4 inline-block rounded-full text-sm font-medium'>
          Secret access granted
        </span>
      )}

      <div className='mb-8 grid gap-4'>
        <InfoCard
          title='Version'
          items={[
            { label: 'Version', value: version },
            { label: 'Phase', value: phase },
            { label: 'Platform', value: isElectron ? 'Electron' : 'Web' },
            { label: 'Vendor', value: vendor ?? 'N/A' }
          ]}
        />

        <InfoCard
          title='API Configuration'
          items={[
            { label: 'API Endpoint', value: config.apiEndpoint },
            { label: 'Frontend URL', value: config.frontendUrl },
            { label: 'CDN Endpoint', value: config.cdn.endpoint }
          ]}
        />

        <InfoCard
          title='Build Information'
          items={[
            { label: 'Branch', value: buildBranch },
            { label: 'Commit', value: buildCommit },
            { label: 'Build Date', value: buildDate },
            { label: 'Production Build', value: config.isProduction ? 'Yes' : 'No' }
          ]}
        />
      </div>

      <div className='flex justify-center'>
        <a
          href='/'
          className='bg-blue-500 hover:bg-blue-600 rounded px-4 py-2 text-white transition-colors'
        >
          Back to App
        </a>
      </div>
    </div>
  );
};
